import React from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import { TextField, MenuItem, CircularProgress } from "@material-ui/core";
import SearchableSelect from "components/SearchableSelect/SearchableSelect";
import { userService } from "_services/user.service";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";

// Group levels (backend groupType enum) with UI label + accent colour for the chart.
const LEVEL_META = {
  EXECUTIVE: { label: "Executive", color: "#6a1b9a" },
  SVPO: { label: "SVPO group", color: "#00706f" },
  VPO: { label: "VPO group", color: "#2e7d32" },
  ADMIN: { label: "Admin group", color: "#c47f17" },
};
const TYPE_OPTIONS = ["EXECUTIVE", "SVPO", "VPO", "ADMIN"];
const FACILITY_COLOR = "#607d8b";
const FACILITY_LIMIT = 5; // collapse an admin group's facilities past this many

const levelLabel = (t) => (LEVEL_META[t] || {}).label || t || "Group";
const levelColor = (t) => (LEVEL_META[t] || {}).color || "#00706f";

export default class ManagementHierarchy extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      groups: [],          // flat list of all groups (for the Group dropdown)
      groupType: "",       // selected Group Type
      groupId: "",         // selected Group id
      tree: null,          // fetched subtree for the selected group
      loadingTree: false,
      expanded: {},        // { [nodeId]: true } — facility lists expanded past the limit
    };
  }

  componentDidMount() {
    this.fetchGroups();
  }

  notify = (message, type) => userService.showNotification({ title: "Management Hierarchy", message, type });

  fetchGroups = async () => {
    try {
      const r = await fetch(apiPath.groups, { method: "GET", headers: generalAuthTokenHeader() });
      const data = r.ok ? await r.json() : [];
      this.setState({ groups: Array.isArray(data) ? data : [] });
    } catch (e) {
      /* ignore — dropdown simply stays empty */
    }
  };

  onTypeChange = (e) => {
    this.setState({ groupType: e.target.value, groupId: "", tree: null, expanded: {} });
  };

  onGroupChange = (groupId) => {
    this.setState({ groupId, expanded: {} });
    if (groupId) this.fetchTree(groupId);
    else this.setState({ tree: null });
  };

  fetchTree = async (groupId) => {
    this.setState({ loadingTree: true, tree: null });
    try {
      const r = await fetch(`${apiPath.groups}/${groupId}/tree`, { method: "GET", headers: generalAuthTokenHeader() });
      if (r.ok) {
        const data = await r.json();
        this.setState({ tree: data, loadingTree: false });
      } else {
        this.notify("Could not load hierarchy", "danger");
        this.setState({ loadingTree: false });
      }
    } catch (e) {
      this.notify("Server error", "danger");
      this.setState({ loadingTree: false });
    }
  };

  toggleExpanded = (nodeId) =>
    this.setState((s) => ({ expanded: { ...s.expanded, [nodeId]: !s.expanded[nodeId] } }));

  // ----------------------------------------------------------------- rendering

  renderFacilities = (node) => {
    const facilities = node.facilities || [];
    if (!facilities.length) return null;
    const isExpanded = !!this.state.expanded[node.id];
    const visible = isExpanded ? facilities : facilities.slice(0, FACILITY_LIMIT);
    const hidden = facilities.length - visible.length;

    const items = visible.map((f) => (
      <li key={`f-${f.id}`}>
        <div className="mh-node mh-facility" style={{ borderTopColor: FACILITY_COLOR }}>
          <span className="mh-fac-icon" role="img" aria-label="facility">🏥</span>
          <span className="mh-fac-name">{f.facilityName || `#${f.facilityId}`}</span>
        </div>
      </li>
    ));

    if (hidden > 0) {
      items.push(
        <li key={`more-${node.id}`}>
          <div className="mh-node mh-more" onClick={() => this.toggleExpanded(node.id)}>
            + {hidden} more
          </div>
        </li>
      );
    } else if (isExpanded && facilities.length > FACILITY_LIMIT) {
      items.push(
        <li key={`less-${node.id}`}>
          <div className="mh-node mh-more" onClick={() => this.toggleExpanded(node.id)}>
            Show less
          </div>
        </li>
      );
    }
    return items;
  };

  renderNode = (node) => {
    if (!node || !node.id) return null;
    const inactive = node.active === false;
    const children = node.children || [];
    const facCount = (node.facilities || []).length;
    const hasBelow = children.length > 0 || facCount > 0;

    return (
      <li key={`g-${node.id}`} className={inactive ? "mh-inactive" : ""}>
        <div className="mh-node mh-group" style={{ borderTopColor: levelColor(node.groupType) }}>
          <div className="mh-node-title">{node.groupName}</div>
          <div className="mh-node-sub">
            {levelLabel(node.groupType)} · {facCount} fac.
          </div>
          {inactive && <span className="mh-badge-inactive">Inactive</span>}
        </div>
        {hasBelow && (
          <ul>
            {children.map(this.renderNode)}
            {this.renderFacilities(node)}
          </ul>
        )}
      </li>
    );
  };

  render() {
    const { groups, groupType, groupId, tree, loadingTree } = this.state;
    const groupsForType = groupType ? groups.filter((g) => g.groupType === groupType) : [];

    return (
      <GridContainer>
        <Card className={"advert-cover"}>
          <GridItem xs={12} sm={12} md={12}>
            <CardHeader color="success" className={"cusCardheader cusCardTitle"}>
              <h4>Management Hierarchy</h4>
            </CardHeader>
          </GridItem>

          <div className={"custom-container"}>
            {/* Selectors */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 8 }}>
              <div style={{ minWidth: 240 }}>
                <TextField fullWidth select label="Group Type" value={groupType} onChange={this.onTypeChange}>
                  <MenuItem value="">-- select group type --</MenuItem>
                  {TYPE_OPTIONS.map((t) => (
                    <MenuItem key={t} value={t}>{levelLabel(t)}</MenuItem>
                  ))}
                </TextField>
              </div>
              <div style={{ minWidth: 240 }}>
                <SearchableSelect
                  label="Group"
                  placeholder="-- select group --"
                  value={groupId}
                  onChange={this.onGroupChange}
                  disabled={!groupType}
                  noOptionsText={groupType ? "No groups of this type" : "Select a group type first"}
                  options={groupsForType.map((g) => ({ value: g.id, label: g.groupName }))}
                />
              </div>
            </div>

            {/* Legend */}
            <div className="mh-legend">
              {TYPE_OPTIONS.map((t) => (
                <span key={t} className="mh-legend-item">
                  <i style={{ background: levelColor(t) }} /> {levelLabel(t)}
                </span>
              ))}
              <span className="mh-legend-item">
                <i style={{ background: FACILITY_COLOR }} /> Facility
              </span>
            </div>

            {/* Chart */}
            <div className="mh-chart-scroll">
              {loadingTree ? (
                <div className="mh-empty"><CircularProgress size={28} /></div>
              ) : !groupId ? (
                <div className="mh-empty">Select a group type and a group to view its hierarchy.</div>
              ) : tree && tree.id ? (
                <ul className="mh-tree">{this.renderNode(tree)}</ul>
              ) : (
                <div className="mh-empty">No hierarchy to display for this group.</div>
              )}
            </div>
          </div>
        </Card>

        <style>{MH_STYLES}</style>
      </GridContainer>
    );
  }
}

// Scoped styles for the hierarchy chart (prefixed mh- to avoid collisions).
const MH_STYLES = `
.mh-legend { display:flex; flex-wrap:wrap; gap:16px; margin:6px 0 14px; font-size:0.82rem; color:#555; }
.mh-legend-item { display:inline-flex; align-items:center; }
.mh-legend-item i { display:inline-block; width:12px; height:12px; border-radius:3px; margin-right:6px; }

.mh-chart-scroll { overflow-x:auto; padding:12px 4px 24px; }
.mh-empty { text-align:center; color:#8a8a8a; padding:48px 0; font-size:0.9rem; }

.mh-tree, .mh-tree ul, .mh-tree li { list-style:none; margin:0; padding:0; }
.mh-tree { display:inline-block; min-width:100%; text-align:center; white-space:nowrap; }
.mh-tree ul { display:flex; justify-content:center; padding-top:22px; position:relative; }
.mh-tree li {
  position:relative; padding:22px 10px 0; display:flex; flex-direction:column; align-items:center;
}
/* connectors */
.mh-tree li::before, .mh-tree li::after {
  content:''; position:absolute; top:0; right:50%; width:50%; height:22px; border-top:2px solid #cfd8dc;
}
.mh-tree li::after { right:auto; left:50%; border-left:2px solid #cfd8dc; }
.mh-tree li:only-child::before, .mh-tree li:only-child::after { display:none; }
.mh-tree li:first-child::before, .mh-tree li:last-child::after { border:0 none; }
.mh-tree li:last-child::before { border-right:2px solid #cfd8dc; border-radius:0 6px 0 0; }
.mh-tree li:first-child::after { border-radius:6px 0 0 0; }
.mh-tree ul ul::before {
  content:''; position:absolute; top:0; left:50%; width:0; height:22px; border-left:2px solid #cfd8dc;
}
/* root has no connector above it */
.mh-tree > li { padding-top:0; }
.mh-tree > li::before, .mh-tree > li::after { display:none; }

.mh-node {
  position:relative; display:inline-block; background:#fff; border:1px solid #e4e7eb;
  border-top:3px solid #00706f; border-radius:8px; padding:10px 16px; min-width:150px;
  box-shadow:0 1px 3px rgba(0,0,0,0.09); white-space:normal;
}
.mh-node-title { font-weight:600; color:#2b2b2b; font-size:0.9rem; }
.mh-node-sub { color:#7a7a7a; font-size:0.76rem; margin-top:2px; }

.mh-facility {
  min-width:120px; padding:8px 12px; background:#fafbfc; border-top-width:3px;
  display:flex; align-items:center; gap:6px;
}
.mh-fac-icon { font-size:0.85rem; }
.mh-fac-name { font-size:0.82rem; color:#37474f; }
.mh-fac-type { font-size:0.62rem; font-weight:600; letter-spacing:0.04em; color:#90a4ae; margin-left:2px; }

.mh-more {
  min-width:auto; padding:8px 14px; cursor:pointer; border-top-color:#b0bec5;
  color:#00706f; font-size:0.8rem; font-weight:600;
}
.mh-more:hover { background:#f1f8f7; }

.mh-badge-inactive {
  position:absolute; top:-9px; right:-6px; background:#eceff1; color:#78909c;
  font-size:0.6rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em;
  padding:1px 6px; border-radius:8px; border:1px solid #cfd8dc;
}
.mh-inactive > .mh-node { opacity:0.5; }
`;
